// import UploadFileList from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/UpdateGRModal/UploadFileList';
// Changed the state to status
// Prevent editing the file extension
// Remove the Actions column and use icons instead (Rename and Delete)
// Added 1 more props showFileList to control the rendering of file list or table

import React, { useRef, useMemo, useState, useEffect } from 'react';
import classNames from 'classnames';
import { useLatest, useDeepCompareEffect } from 'ahooks';
import { Button, Table, Dropdown, message, Bubble } from 'tea-component';
import Upload, { getMimeType, video } from '@sg/components/Upload';
import DragableUploader from '@gp/components/DragableUploader';
import FileState from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/UpdateGRModal/FileState';
import RenameBox from '@gp/pages/Pipeline/ProjectDetails/Milestone/components/RenameBox';
import useGetState from '@intelligence/hooks/useGetState';
import useCos from '@gp/hooks/useCos';
import { getBIDSTencentQCouldMassage } from '@sg/services/cos';
import { isValidNumber, getFileSize, splitFileName } from '@gp/utils/common';
import { COMMON_IMAGE_ACCEPTS, UPLOAD_BUTTON_TIPS } from '@gp/constants/const';
import { FILE_ICONS } from '@gp/constants/icons';
import Loading from '@intelligence/components/Loading';

import type { IBaseProps } from '@home/types/common';

// import '@gp/pages/Pipeline/ProjectDetails/Milestone/components/UpdateGRModal/UploadFileList/index.scss';
import '@sg/components/UploadFileList/index.scss';

import ICON_UPLOAD from '@gp/assets/svg/upload.svg';
import EditButton from '@sg/components/EditButton';
import DeleteButton from '@sg/components/DeleteButton';

const { autotip } = Table.addons;

interface ItemType {
  path: string;
  name: string;
  size: string;
  cloud_path?: string;
  file_size?: string;
}

interface IRecordsType {
  uniqueId: string;
  path: string;
  name: string;
  size: string;
  progress: number; // 上传进度
  status: 1 | 2; // 1：成功，2：失败
  isDeleted?: boolean; // 是否被删除
  taskId?: string; // 上传任务编码，用于取消上传中的文件
}

interface IUploadFileListProps extends IBaseProps {
  uniqueId: string;
  // 上传到COS的文件夹目录
  uploadFolderName?: string;
  defaultList?: ItemType[] | null;
  getIsAllow?: (data: boolean) => void;
  onResult?: (data: ItemType[]) => void;
  // When false, only shows upload button; when true, shows simple file list with edit/delete
  showFileList?: boolean;
  // Controls file list position: 'top' (above button), 'bottom' (below button), or 'right' (horizontal layout)
  fileListPosition?: 'top' | 'bottom' | 'right';
  // Extra buttons to render alongside the Upload button (e.g., Regenerate button)
  extraButtons?: React.ReactNode;
  // Controls if input should auto-resize based on content width (default: false)
  autoResizeInput?: boolean;
  // Hide the upload button (default: false) - useful when you only want rename/delete capabilities
  hideUploadButton?: boolean;
  // Controls whether to include 'size' field in onResult callback (default: true) -- this is more for the autosave feature
  includeSizeInResult?: boolean;
  // Hide edit/rename button (default: false) - makes file list read-only for editing
  excludeEdit?: boolean;
  // Hide delete button (default: false) - prevents file deletion
  excludeDelete?: boolean;
}

const ACCEPTS = [
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
  '.pdf',
  '.ppt',
  '.pptx',
  ...COMMON_IMAGE_ACCEPTS,
  ...(video?.split(',') || []),
];
const ACCEPTS_STR = ACCEPTS.join(', ');

const UploadFileList: React.FC<IUploadFileListProps> = (props) => {
  const {
    style,
    className,
    uniqueId = '',
    uploadFolderName = 'gate_review_material',
    defaultList = null,
    getIsAllow,
    onResult: onResultProps,
    setIsCompletedVideo,
    multiple = true,
    isSingleMultiple = false,
    tableAutoTipHeight = 80,
    fileNameWith = 380,
    stateWith = 246,
    maxSize = 5 * 1024 * 1024 * 1024,
    acceptValue = ACCEPTS,
    limitedTypeValue = ACCEPTS_STR,
    buttonTips = UPLOAD_BUTTON_TIPS,
    setDeletePathList,
    deletePathList,
    showFileList = false,
    fileListPosition = 'top',
    extraButtons,
    autoResizeInput = false,
    hideUploadButton = false,
    includeSizeInResult = true,
    excludeEdit = false,
    excludeDelete = false,
  } = props;

  const onResult = useLatest(onResultProps);

  const { deleteObject } = useCos({
    qCouldService: getBIDSTencentQCouldMassage,
    qCouldServiceParams: uploadFolderName,
  });

  const uploader = useRef<any>();

  const uploadingListRef = useRef<any[]>([]);

  // Ref for parent container to get max width
  const fileListContainerRef = useRef<HTMLDivElement>(null);
  // Ref for measuring text width
  const measureSpanRef = useRef<HTMLSpanElement>(null);
  const [inputWidth, setInputWidth] = useState<number>(100);

  // 上传文件中的loading列表，正常情况存储的是上传进度（小数），当有报错时，对应位置储存的是"error"
  const [uploadingList, setUploadingList] = useState<any[]>([]);
  // 上传任务编码列表
  const [taskIds, setTaskIds] = useState<string[]>([]);
  // 上传文件的名称
  const [uploadPathList, setUploadPathList] = useState<any[]>([]);
  // 上传文件进度更新返回的结果
  const [uploadProgressRes, setUploadProgressRes] = useState<any>(null);
  // 上传文件任务编码返回的结果
  const [uploadTaskReadyRes, setUploadTaskReadyRes] = useState<any>(null);
  // 是否处于上传中
  const [isUploadPending, setUploadPending] = useState<boolean>(false);
  // 上传文件超过最大文件大小情况
  const [sizeOverList, setSizeOverList] = useState<boolean[]>([]);
  // 上传文件格式是否有误情况
  const [formatOverList, setFormatOverList] = useState<boolean[]>([]);
  // 所有文件的信息
  const [filesMsg, setFilesMsg, getFilesMsg] = useGetState<ItemType[]>([]);
  // 列表数据
  const [records, setRecords, getRecords] = useGetState<IRecordsType[]>([]);
  // 重命名文件弹窗的visible 映射，键为record的uniqueId，值为对应的弹窗的visible
  const [renameBoxVisibles, setRenameBoxVisibles, getRenameBoxVisibles] = useGetState<Record<string, boolean>>({});
  const [isDragging, setIsDragging] = useState(false);
  // Inline editing state for simple file list
  const [editingFileId, setEditingFileId] = useState<string | null>(null);
  const [editingFileName, setEditingFileName] = useState<string>('');

  // Calculate input width based on content using hidden span (only when autoResizeInput is enabled)
  useEffect(() => {
    if (!autoResizeInput) return;

    if (measureSpanRef.current && editingFileName) {
      const textWidth = measureSpanRef.current.offsetWidth;

      // Get parent container max width
      let maxWidth = 400; // default max width
      if (fileListContainerRef.current) {
        const containerWidth = fileListContainerRef.current.offsetWidth;
        // Reserve space for avatar (24px) + margins + action buttons (estimate 80px)
        maxWidth = Math.max(containerWidth - 80, 100);
      }

      // Calculate actual width: text width + padding, but not exceeding max width
      const calculatedWidth = Math.min(Math.max(textWidth + 20, 60), maxWidth);
      setInputWidth(calculatedWidth);
    } else {
      setInputWidth(100);
    }
  }, [editingFileName, autoResizeInput]);

  // 禁用选择文件按钮
  const disabledSelectFile = useMemo<boolean>(() => {
    if (!records || !Array.isArray(records)) return false;

    if (multiple === false && records?.length > 0) {
      if (records?.some((item) => item.isDeleted !== true)) {
        return true;
      }
    }

    // 只要有一个文件还在上传中就禁用
    return records.some((t) => t?.progress !== 1 && t?.status !== 2);
  }, [records]);

  // console.log('recordsrecordsrecords2222', records);

  useDeepCompareEffect(() => {
    setIsCompletedVideo?.(!disabledSelectFile);
  }, [disabledSelectFile]);

  const handleMultipleBeforeUploadFileMsg = (fileNames: string[], pathNames: string[], fileSizes: number[]) => {
    const newFilesMsg = [];
    for (let i = 0; i < pathNames?.length; i++) {
      const t = pathNames?.[i];
      newFilesMsg.push({
        path: t,
        name: fileNames?.[i] || t,
        size: isValidNumber(fileSizes?.[i]) ? String(fileSizes?.[i]) : '',
      });
    }
    const filesMsg = getFilesMsg();
    setFilesMsg([...filesMsg, ...newFilesMsg]);
  };

  const handleMultipleUploadStart = (files: any[], pathList: string[]) => {
    if (pathList?.length) {
      const v = Array.from({ length: pathList?.length }, () => 0);
      setUploadPathList(pathList);
      setUploadingList([...v]);
      uploadingListRef.current = [...v];
      setUploadPending(true);
    }
  };

  const handleMultipleUploadSizeOver = (resList: boolean[]) => {
    setSizeOverList(resList);
  };

  const handleMultipleUploadFormatOver = (resList: boolean[]) => {
    setFormatOverList(resList);
  };

  const handleMultipleUploadProgress = (pathName: string, percent: number) => {
    // 上传进度100%时显示99%，上传成功回调执行时才显示100%
    setUploadProgressRes({ ...{ pathName, percent: percent > 0.99 ? 0.99 : percent } });
  };

  const handleMultipleUploadTaskReady = (pathName: string, taskId: string) => {
    setUploadTaskReadyRes({ ...{ pathName, taskId } });
  };

  // 单个文件上传成功
  const handleSingleUploadSuccess = (res1: any, res2: any, path: any, ele: any, position: number) => {
    const newUploadingList = [...uploadingListRef.current];
    newUploadingList[position] = 1;
    setUploadingList([...newUploadingList]);
    uploadingListRef.current = newUploadingList;
  };

  // 单个文件上传失败
  const handleSingleUploadError = (path: any, position: number) => {
    console.error(`❌ [Upload] File #${position + 1} upload FAILED:`, {
      path,
      folder: uploadFolderName
    });

    const newUploadingList = [...uploadingListRef.current];
    newUploadingList[position] = 'error';
    setUploadingList([...newUploadingList]);
    uploadingListRef.current = newUploadingList;
  };

  const handleMultipleUploadSuccess = () => {
    console.log('🎉 [Upload] All files uploaded successfully to:', uploadFolderName);
    setUploadPending(false);
  };

  // 这个是全部上传失败
  const handleMultipleUploadError = () => {
    console.error('💥 [Upload] All files upload FAILED for folder:', uploadFolderName);

    const newUploadingList = uploadingListRef.current?.map(() => 'error');
    setUploadingList([...newUploadingList]);
    uploadingListRef.current = newUploadingList;
    setUploadPending(false);
  };

  const handleRenameCancel = (uniqueId: string) => {
    const renameBoxVisibles = getRenameBoxVisibles();
    const renameBoxVisiblesTmp = { ...renameBoxVisibles, [uniqueId]: false };
    setRenameBoxVisibles(renameBoxVisiblesTmp);
  };

  // 默认数据
  useEffect(() => {
    if (!Array.isArray(defaultList)) return;
    const newUploadPathList: any[] = [];
    const newFilesMsg: ItemType[] = [];

    defaultList.forEach((item: ItemType) => {
      const { path, cloud_path: cloudPath, name, size, file_size: fileSize } = item || {};
      const pathTemp = path || cloudPath;
      const sizeTemp = size || fileSize;
      newUploadPathList.push({ path: pathTemp || '', name: name || '' });
      newFilesMsg.push({ path: pathTemp || '', name: name || '', size: sizeTemp || '' });
    });
    setFilesMsg([...newFilesMsg]);
    newUploadPathList?.length && setUploadPathList(newUploadPathList);
  }, [defaultList]);

  // 当上传的文件大小及格式有误时，同步更新uploadingList
  useEffect(() => {
    if (!sizeOverList?.length && !formatOverList?.length) return;
    if (isUploadPending) {
      const newUploadingList = uploadingList?.map((t: number, i: number) => {
        if (sizeOverList?.[i] || formatOverList?.[i]) {
          return 'error';
        }
        return t;
      });
      setUploadingList([...newUploadingList]);
      uploadingListRef.current = newUploadingList;
    }
  }, [sizeOverList, formatOverList]);

  // 根据上传进度更新loading效果
  useEffect(() => {
    if (!uploadProgressRes) return;
    const newUploadingList = [...uploadingList];
    let isStop = false;
    for (let i = 0; i < uploadPathList?.length; i++) {
      const t = uploadPathList?.[i];
      if (t?.path === uploadProgressRes?.pathName) {
        // 避免重复更新上传进度
        if (newUploadingList[i] >= 0.99) {
          isStop = true;
        } else {
          newUploadingList[i] = uploadProgressRes?.percent;
        }
      }
    }
    if (isStop) return;
    setUploadingList([...newUploadingList]);
    uploadingListRef.current = newUploadingList;
    const flag = newUploadingList?.every((t: number) => t >= 1);
    setUploadPending(!flag);
  }, [uploadProgressRes]);

  // 组织taskIds
  useEffect(() => {
    if (!uploadTaskReadyRes) return;
    const newTaskIds = [];
    for (let i = 0; i < uploadPathList?.length; i++) {
      const t = uploadPathList?.[i];
      if (t?.path === uploadTaskReadyRes?.pathName) {
        newTaskIds[i] = uploadTaskReadyRes?.taskId;
      } else {
        newTaskIds[i] = taskIds?.[i] || '';
      }
    }
    setTaskIds([...newTaskIds]);
  }, [uploadTaskReadyRes]);

  // 根据上传进度，文件基本信息，上传返回结果组织列表显示数据
  useEffect(() => {
    if (Array.isArray(filesMsg)) {
      const newRecords: IRecordsType[] = [];
      const alreadyUploadFileLen = filesMsg?.length - uploadingList?.length || 0;
      const records = getRecords();
      for (let i = 0; i < filesMsg?.length; i++) {
        const t = filesMsg?.[i];
        // 已经上传的
        if (i <= alreadyUploadFileLen - 1) {
          // Find existing record by path instead of index to prevent deletion bug
          const existingRecord = records?.find((r) => r?.path === t?.path && !r?.isDeleted);
          const isDeleted = existingRecord?.isDeleted || false;
          const tempProgress = existingRecord?.status;
          const taskId = existingRecord?.taskId || '';
          const progress = isDeleted ? 1 : tempProgress === 2 ? 0 : 1;
          const uniqueId = `${t?.path}_${taskId}_${i + 1}`;
          const record = records?.find((item) => item?.uniqueId === uniqueId);
          const name = record?.name || t?.name || '';
          newRecords.push({
            uniqueId,
            path: t?.path,
            name,
            size: t?.size,
            progress, // 上传进度
            status: isDeleted ? 1 : tempProgress || 1, // 1：成功，2：失败
            taskId,
            isDeleted,
          });
          // 上传中的
        } else {
          const index = i - alreadyUploadFileLen;
          // Find existing record by path instead of index to prevent deletion bug
          const existingRecord = records?.find((r) => r?.path === t?.path && !r?.isDeleted);
          const isDeleted = existingRecord?.isDeleted || false;
          const tempProgress = uploadingList?.[index];
          const taskId = taskIds?.[index] || '';
          const progress = isDeleted ? 1 : tempProgress === 'error' ? 0 : tempProgress;
          const uniqueId = `${t?.path}_${taskId}_${i + 1}`;
          const record = records?.find((item) => item?.uniqueId === uniqueId);
          const name = record?.name || t?.name || '';
          newRecords.push({
            uniqueId,
            path: t?.path,
            name,
            size: t?.size,
            progress, // 上传进度
            status: isDeleted ? 1 : tempProgress === 'error' ? 2 : 1, // 1：成功，2：失败
            taskId,
            isDeleted,
          });
        }
      }
      setRecords(newRecords);
    }
  }, [uploadingList, filesMsg]);

  // 判断滚动条是否显示，表头样式更改，删除行效果，是否触发onResult，是否全部上传成功
  useEffect(() => {
    if (!records || !Array.isArray(records) || !records?.length) {
      getIsAllow?.(true);
      return;
    }

    // 是否触发Result
    if (records?.every((t) => t?.status === 1 && t?.progress === 1)) {
      getIsAllow?.(true);
      const resultData = records
        ?.filter((t) => !t?.isDeleted)
        ?.map((t) => {
          const fileData: any = {
            name: t?.name,  // name first for consistent property order
            path: t?.path,
          };
          // Only include size if includeSizeInResult is true
          if (includeSizeInResult) {
            fileData.size = t?.size;
          }
          return fileData;
        });
      onResult.current?.(resultData);
    } else {
      getIsAllow?.(false);
    }
  }, [records, includeSizeInResult]);

  // Render simple file list component
  const renderSimpleFileList = () => {
    if (!showFileList || !records || records.filter((t) => !t?.isDeleted).length === 0) {
      return null;
    }

    return (
      <div className="dbs-upload-file-list-simple" ref={fileListContainerRef}>
        {records.filter((t) => !t?.isDeleted).map((record: IRecordsType) => {
          const { uniqueId, path, name, status, progress } = record || {};
          const { suffix } = splitFileName(path);
          if (!name) return null;

          const dotIndex = name?.lastIndexOf('.');
          const simpleName = dotIndex !== undefined && dotIndex !== -1 ? name?.substring(0, dotIndex) : name;

          // Check if file is uploading (pending or in progress)
          const isUploading = progress < 1 && status !== 2;
          const isSuccess = progress === 1 && status === 1;
          const isEditing = editingFileId === uniqueId;

          return (
            <div
              key={uniqueId}
              className={classNames("dbs-upload-file-list-simple-link", {
                "dbs-upload-file-list-simple-link--editing": isEditing
              })}
            >
              {/* Show loading spinner with delete button when uploading */}
              {isUploading ? (
                <div className='dbs-upload-file-loading-wrapper'>
                  <Loading content="" />
                  {!excludeDelete && (
                    <DeleteButton
                      title="Cancel"
                      onClick={(e) => {
                        e?.stopPropagation();
                        if (typeof setDeletePathList === 'function') {
                          const newList = [...deletePathList, path];
                          const deleteList = [...new Set(newList)];
                          setDeletePathList(deleteList);
                        } else {
                          deleteObject({ path });
                        }

                        const records = getRecords() || [];
                        const newRecords = records?.map((t) => {
                          if (t?.uniqueId === uniqueId) {
                            return {
                              ...t,
                              isDeleted: true,
                              progress: 1,
                              status: 1 as 1 | 2,
                            };
                          }
                          return t;
                        }) || [];
                        setRecords([...newRecords]);
                      }}
                    />
                  )}
                </div>
              ) : isSuccess ? (
                <>
                  {/* Show file info when upload is complete */}
                  <div className="dbg-gr-info">
                    <div className="dbg-gr-info-avatar">
                      <img
                        src={FILE_ICONS[`${suffix?.substring(1)}`]}
                        alt={suffix || 'File'}
                        className="update-user-item-cover"
                        width={16}
                        height={16}
                      />
                    </div>

                    {/* Inline editing: Show input when editing, otherwise show text */}
                    {isEditing ? (
                      <>
                        {/* Hidden span for measuring text width (only when autoResizeInput is enabled) */}
                        {autoResizeInput && (
                          <span
                            ref={measureSpanRef}
                            style={{
                              position: 'absolute',
                              visibility: 'hidden',
                              whiteSpace: 'pre',
                              fontSize: '14px',
                              fontFamily: 'inherit',
                            }}
                          >
                            {editingFileName}
                          </span>
                        )}
                        <input
                          type="text"
                          className="dbs-gr-info-name-input"
                          style={autoResizeInput ? {
                            width: `${inputWidth}px`,
                          } : undefined}
                          value={editingFileName}
                          onChange={(e) => setEditingFileName(e.target.value)}
                          onBlur={() => {
                            const trimmedValue = editingFileName.trim();

                            if (trimmedValue) {
                              // Save new name if not empty
                              const records = getRecords() || [];
                              const recordIndex = records?.findIndex((record) => record?.uniqueId === uniqueId) ?? -1;
                              if (recordIndex !== -1) {
                                const record = records[recordIndex];
                                const originalName = record?.name || '';
                                const dotIndex = originalName?.lastIndexOf('.');
                                const extension = dotIndex > 0 ? originalName?.substring(dotIndex) : '';
                                const newName = `${trimmedValue}${extension}`;

                                const recordTmp = { ...record, name: newName };
                                const recordsTmp = [
                                  ...records.slice(0, recordIndex),
                                  recordTmp,
                                  ...records.slice(recordIndex + 1),
                                ];
                                setRecords(recordsTmp);
                              }
                            }
                            // Exit edit mode (if empty, original name is preserved in record)
                            setEditingFileId(null);
                            setEditingFileName('');
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.currentTarget.blur();
                            } else if (e.key === 'Escape') {
                              // Cancel editing on Escape
                              setEditingFileId(null);
                              setEditingFileName('');
                            }
                          }}
                          autoFocus
                        />
                      </>
                    ) : (
                      <div className="dbg-gr-info-name">{simpleName}</div>
                    )}

                    {/* Hide suffix when editing */}
                    {!!suffix && !isEditing && <div className="dbs-gr-info-type">{suffix}</div>}
                  </div>

                  {/* Show action buttons only when not editing */}
                  {!isEditing && (
                    <div className='dbs-upload-file-button-wrapper'>
                      {!excludeEdit && (
                        <EditButton
                          title="Rename"
                          useBubble={true}
                          onClick={(e) => {
                            e?.stopPropagation();
                            setEditingFileId(uniqueId);
                            setEditingFileName(simpleName);
                          }}
                        />
                      )}
                      {!excludeDelete && (
                        <DeleteButton
                          title="Delete"
                          onClick={(e) => {
                            e?.stopPropagation();
                            if (typeof setDeletePathList === 'function') {
                              const newList = [...deletePathList, path];
                              const deleteList = [...new Set(newList)];
                              setDeletePathList(deleteList);
                            } else {
                              deleteObject({ path });
                            }

                            const records = getRecords() || [];
                            const newRecords = records?.map((t) => {
                              if (t?.uniqueId === uniqueId) {
                                return {
                                  ...t,
                                  isDeleted: true,
                                  progress: 1,
                                  status: 1 as 1 | 2,
                                };
                              }
                              return t;
                            }) || [];
                            setRecords([...newRecords]);
                          }}
                        />
                      )}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div
      style={style}
      className={classNames('dbs-gr-edit-upload', className, {
        highlight: isDragging,
        'has-data': !!records?.filter((t) => !t?.isDeleted)?.length,
        'dbs-gr-edit-upload--horizontal': fileListPosition === 'right',
      })}
    >
      {/* Render file list at top if position is 'top' */}
      {fileListPosition === 'top' && renderSimpleFileList()}

      {/* Only render upload button section if hideUploadButton is false */}
      {!hideUploadButton && (
        <div className="dbs-gr-edit-upload-button">
          <Upload
            ref={uploader}
            uploadFolderName={uploadFolderName}
            qCouldService={getBIDSTencentQCouldMassage}
            // type="file"
            // limitedType=".doc,.docx,.pdf,.ppt,.pptx,.csv,.xlsx"
            accept={acceptValue}
            limitedType={limitedTypeValue}
            id={uniqueId}
            multiple={multiple}
            isSingleMultiple={isSingleMultiple}
            getMimeType={getMimeType}
            onMultipleBeforeUploadFileMsg={handleMultipleBeforeUploadFileMsg}
            onMultipleStart={handleMultipleUploadStart}
            onMultipleSizeOver={handleMultipleUploadSizeOver}
            onMultipleFormatOver={handleMultipleUploadFormatOver}
            onMultipleProgress={handleMultipleUploadProgress}
            onMultipleTaskReady={handleMultipleUploadTaskReady}
            onMultipleSuccess={handleMultipleUploadSuccess}
            onMultipleError={handleMultipleUploadError}
            onMultipleSingleSuccess={handleSingleUploadSuccess} // 单个上传成功
            onMultipleSingleError={handleSingleUploadError} // 单个上传失败
            slot={
              <Button
                className={classNames({
                  disabled: disabledSelectFile,
                })}
                disabled={disabledSelectFile}
              >
                <img
                  style={{
                    position: 'relative',
                    top: -2,
                    marginRight: 4,
                    width: 16,
                    height: 16,
                    verticalAlign: 'middle',
                    color: '#888',
                  }}
                  src={ICON_UPLOAD}
                />
                <span>Upload</span>
              </Button>
            }
            maxSize={maxSize}
            onSizeOver={() => {
              message.warning({
                content: 'The video size cannot exceed 5 GB',
              });
            }}
          />
          {extraButtons}
          <div className="dbg-gr-edit-upload-button-tips">{buttonTips}</div>
        </div>
      )}
      {!showFileList && (<div className="dbg-gr-edit-upload-content">
        <Table
          className="dbg-gr-edit-upload-table"
          bordered={false}
          recordKey="uniqueId"
          records={records?.filter((t) => !t?.isDeleted) || []}
          addons={[
            autotip({
              style: {
                boxSizing: 'border-box',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: tableAutoTipHeight,
              },
              isLoading: false,
              hideIcon: true, // loading状态显示图标，其他情况不显示
              emptyText: (
                <DragableUploader
                  className="dbg-milestone__dragable-uploader"
                  multiple={multiple}
                  max={99}
                  accept={acceptValue}
                  getMimeType={getMimeType}
                  onDraggingChange={setIsDragging}
                  onSelect={(files, e) => {
                    if (e?.target) {
                      // @ts-ignore
                      e.target.files = e?.dataTransfer?.files;
                    }
                    uploader.current?.multipleUpload(e);
                  }}
                  footer={<></>}
                  placeholder={
                    <p className="dbg-milestone__dragable-uploader-placeholder">
                      Click the &quot;Upload&quot; button at the top or drag the file to this area
                    </p>
                  }
                  placeholderWhenDragging={
                    <p className="dbg-milestone__dragable-uploader-placeholder">Release mouse</p>
                  }
                />
              ),
            }),
          ]}
          columns={[
            {
              key: 'file_name',
              header: 'Filename',
              width: fileNameWith,
              render: (cvm: IRecordsType) => {
                const { uniqueId, path, name, status, progress } = cvm || {};
                const { suffix } = splitFileName(path);
                if (!name) return '-';
                const dotIndex = name?.lastIndexOf('.');
                const simpleName = dotIndex === undefined && dotIndex === -1 ? name : name?.substring(0, dotIndex);
                return (
                  <div className="dbg-gr-info">
                    <div className="dbg-gr-info-avatar">
                      <img
                        className="update-user-item-cover"
                        width={16}
                        height={16}
                        src={FILE_ICONS[`${suffix?.substring(1)}`]}
                      />
                    </div>
                    <div className="dbg-gr-info-name">{simpleName}</div>
                    {!!suffix && (
                      <>
                        <div className="dbs-gr-info-type">{suffix}</div>
                      </>
                    )}
                    {/* Inline action icons */}
                    <div className="dbs-upload-file-button-wrapper">
                      {/* Rename icon */}
                      {!excludeEdit && (
                        <Dropdown
                          appearance="pure"
                          placement="bottom-start"
                          clickClose={false}
                          open={renameBoxVisibles[uniqueId]}
                          button={
                            <EditButton
                              // className="dbs-upload-file-button"
                              title="Edit"
                              onClick={(e) => {
                                e?.stopPropagation();
                                const renameBoxVisibles = getRenameBoxVisibles();
                                const renameBoxVisiblesTmp = Object.keys(renameBoxVisibles).reduce(
                                  (acc, cur) => ({ ...acc, [cur]: false }),
                                  {},
                                );
                                Object.assign(renameBoxVisiblesTmp, { [uniqueId]: true });
                                setRenameBoxVisibles(renameBoxVisiblesTmp);
                              }}
                            />
                          }
                        >
                          <RenameBox
                            value={(() => {
                              // Extract filename without extension for editing
                              const dotIndex = name?.lastIndexOf('.');
                              return dotIndex > 0 ? name?.substring(0, dotIndex) : name;
                            })()}
                            onChange={() => { }}
                            onClose={() => {
                              handleRenameCancel(uniqueId);
                            }}
                            onCancel={() => {
                              handleRenameCancel(uniqueId);
                            }}
                            onConfirm={(payload?: string) => {
                              const records = getRecords() || [];
                              const recordIndex = records?.findIndex((record) => record?.uniqueId === uniqueId) ?? -1;
                              if (recordIndex !== -1) {
                                const record = records[recordIndex];
                                const originalName = record?.name || '';
                                const dotIndex = originalName?.lastIndexOf('.');
                                const extension = dotIndex > 0 ? originalName?.substring(dotIndex) : '';

                                // Combine new filename with original extension
                                const newName = payload ? `${payload.trim()}${extension}` : originalName;

                                const recordTmp = { ...record, name: newName };
                                const recordsTmp = [
                                  ...records.slice(0, recordIndex),
                                  recordTmp,
                                  ...records.slice(recordIndex + 1),
                                ];
                                setRecords(recordsTmp);
                                const renameBoxVisibles = getRenameBoxVisibles();
                                const renameBoxVisiblesTmp = { ...renameBoxVisibles, [uniqueId]: false };
                                setRenameBoxVisibles(renameBoxVisiblesTmp);
                              }
                            }}
                          />
                        </Dropdown>
                      )}
                      {/* Delete button */}
                      {!excludeDelete && (
                        <DeleteButton
                          // className="dbs-upload-file-button"
                          title="Delete"
                          onClick={(e) => {
                            e?.stopPropagation();
                            // 删除腾讯云上传
                            if (typeof setDeletePathList === 'function') {
                              const newList = [...deletePathList, path];
                              const deleteList = [...new Set(newList)];
                              setDeletePathList(deleteList);
                            } else {
                              deleteObject({ path });
                            }

                            const records = getRecords() || [];
                            // 标记删除的项及更改这行状态
                            const newRecords =
                              records?.map((t) => {
                                if (t?.uniqueId === uniqueId) {
                                  return {
                                    ...t,
                                    isDeleted: true,
                                    progress: 1,
                                    status: 1 as 1 | 2,
                                  };
                                }
                                return t;
                              }) || [];
                            setRecords([...newRecords]);
                          }}
                        />
                      )}
                    </div>
                  </div>
                );
              },
            },
            {
              key: 'size',
              header: 'Size',
              width: 100,
              render: (cvm: IRecordsType) => {
                const size = cvm?.size;
                const formattedSize = isValidNumber(size) ? getFileSize(Number(size)) || '0K' : '-';
                return <div className="dbg-gr-size">{formattedSize}</div>;
              },
            },
            {
              key: 'state',
              header: 'Status',
              width: stateWith,
              render: (cvm: IRecordsType) => {
                const { status, progress } = cvm || {};
                const state = status === 2 ? 2 : progress === 1 ? 1 : progress === 0 ? 3 : 4;

                return <FileState state={state} progress={progress} />;
              },
            },
          ]}

        />
      </div>)}

      {/* Render file list at bottom if position is 'bottom' or 'right' */}
      {(fileListPosition === 'bottom' || fileListPosition === 'right') && renderSimpleFileList()}
    </div>
  );
};

export default UploadFileList;
